import { useState, useEffect, useContext, useCallback } from 'react';
import {
  CreateStrainPopup,
  DetailAndDelBtn,
  MetrcLogo,
  StrainEntryPopup,
  errFeedbackFn,
  farmsClasses,
  metrcTagsClasses,
  noPermTablePrefixMsg,
  roomDetailClasses,
} from '../../utility';
import { Box, Button, Chip, InputAdornment, OutlinedInput, Paper, Stack, Typography } from '@mui/material';
import APIService from '../../../api/apiService';
import SearchIcon from '@mui/icons-material/Search';
import DisplayMetrcTagsTable from '../../utility/tables/DisplayMetrcTagsTable';
import DisplayTable from '../../utility/DisplayTable';
import { FeedbackContext } from '../../../App';
import NoPermissionMsg from '../../utility/NoPermissionMsg';
import LoadingSpinner from '../../utility/LoadingSpinner';
import EntityDetailPopup from '../../utility/EntityDetailPopup';
const strainKeyTitleMap = [
  { key: 'strain_name', title: 'Name' },
  { key: 'wet_weight', title: 'Proj Wet Wt(lbs)' },
  { key: 'strain_yield', title: 'Yield' },
  { key: 'flower_price', title: 'Price ($ per lb)' },
  { key: 'cbd_level', title: 'CDB Level' },
  { key: 'thc_level', title: 'THC Level' },
  { key: 'genetics', title: 'Genetics' },
];

const addNewKey = 'add';
const updateKey = 'update';

export default function _StrainsTab(props) {
  const {
    currDarkThemeClass = '',
    openStrainsPopup = false,
    strainPerms = null,
    farms = [],
    handleSetValues = () => { },
    handleErrorWithFeedback = () => { },
    handleEntityEntryCreateRes = () => { },
    handleEntityEntryModifyRes = () => { },
    ...otherProps
  } = props;

  const [open, setOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [detailChildren, setDetailChildren] = useState(null);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [openItemsPopup, setOpenItemsPopup] = useState(false);
  const [itemsData, setItemsData] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [selectedStrain, setSelectedStrain] = useState(null);
  const [itemsTotalCount, setItemsTotalCount] = useState(0);
  const [itemsPage, setItemsPage] = useState(0);
  const [itemsRowsPerPage, setItemsRowsPerPage] = useState(15);

  const feedbackCX = useContext(FeedbackContext);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 600);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (farms.length > 0) {
      setSelectedFarm(farms[0]);
      setSelectedFilter(farms[0].id);
    }
  }, [farms]);

  const fetchData = useCallback(async () => {
    if (!strainPerms?.read || !selectedFilter) {
      return;
    }
    setLoading(true);
    try {
      let url = `strain/?limit=${rowsPerPage}&page=${page + 1}`;
      url += `&farm_id=${selectedFilter}`;
      if (searchTerm) {
        url += `&name__icontains=${encodeURIComponent(searchTerm)}`;
      }

      const response = await APIService.fetchInstance(url);
      if (response?.data) {
        const formattedData = formatData(response.data.results || []);
        setData(formattedData);
        setTotalCount(response.data.count || 0);
      }
    } catch (error) {
      handleErrorWithFeedback(errFeedbackFn({}))(error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, selectedFilter, debouncedSearchTerm, strainPerms]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      // debouncedFetchData();
    }
  };

  const handlePopupClose = () => {
    setDetailChildren({});
    setOpen(false);
    handleSetValues('openStrainsPopup', false);
  };

  const handleSetSelStrain = (prop, value) => {
    setDetailChildren(state => ({
      ...state,
      selStrain: {
        ...state.selStrain,
        [prop]: value,
      },
    }));
  };

  const handleFilterChange = farmId => {
    const farm = farms.find(f => f.id === farmId);
    setSelectedFilter(farmId);
    setSelectedFarm(farm);
    setPage(0);
  };

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const handleSearch = event => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const formatData = (formattedData) => {
    return formattedData.map((strain, idx) => [
      `${idx + 1 + page * rowsPerPage}`.padStart(2, 0),
      ...strainKeyTitleMap.map(e => {
        if (e.key === 'genetics') {
          return strain[e.key] || 'N/A';
        }
        return e.key !== 'strain_name' ? +strain[e.key] : strain[e.key];
      }),
      <DetailAndDelBtn {...gDStrainEntryActionBtnProps(strain)} />
    ]);
  };

  const handleEntityEntryModify = (entity, prop) => async (value) => {
    const errConfig = {
      modify: true,
      resource: 'Strain',
    };
    let res = { success: false };
    try {
      res = await APIService.modifyInstance(entity, value)
        .then(handleEntityEntryModifyRes(prop))
      fetchData();
    } catch (err) {
      handleErrorWithFeedback(errFeedbackFn(errConfig))(err);
    }
    return res;
  };

  const handleStrainEntryView = strain => {
    handleSetValues(updateKey, true);
    setOpen(true);
    console.log('Selected Strain:', strain);

    setDetailChildren({
      selStrain: strain,
      title: strain.strain_name,
      toShowSave: strainPerms?.update ?? false,
      handleStrainSave: handleEntityEntryModify(`strain/${strain.id}/`, 'strains'),
      handleSetSelStrain,
    });
  };

  const handleEntitySave = (entity, prop) => async value => {
    const errConfig = {
      create: true,
      resource: 'Strain',
    };

    let res = { success: false };

    try {
      res = await APIService.createInstance(entity, value).then(response => {
        handleEntityEntryCreateRes(prop)(response);
        fetchData();
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Created Strain successfully.',
          autoHideDuration: 3000,
        });
        return { success: true };
      });
    } catch (err) {
      handleErrorWithFeedback(errFeedbackFn(errConfig))(err);
    }
    return res;
  };

  const handleAddNewStrain = () => {
    handleSetValues(addNewKey, true);
    setOpen(true);
    setDetailChildren({
      title: 'Strain',
      handleAddStrain: handleEntitySave('strain/', 'strains'),
    });
  };

  const gDStrainEntryActionBtnProps = (strain) => ({
    currDarkThemeClass,
    toShowDelBtn: false,
    toShowItemsBtn: true,

    onItemsClick: (e) => {
      e.stopPropagation();
      handleViewItems(strain);
    },

    onDetailClick: () => handleStrainEntryView(strain),
  });

  const handleViewItems = async (strain) => {
    setSelectedStrain(strain);
    setOpenItemsPopup(true);
    setItemsLoading(true);

    try {
      const url = `item/?farm_id=${selectedFilter}&strain=${encodeURIComponent(strain.strain_name)}`;
      const response = await APIService.fetchInstance(url);

      if (response?.data) {
        setItemsData(response.data.results || []);
        setItemsTotalCount(response.data.count || 0);
      }
    } catch (error) {
      handleErrorWithFeedback(errFeedbackFn({}))(error);
    } finally {
      setItemsLoading(false);
    }
  };

  const handleItemsPageChange = (newPage, newRowsPerPage) => {
    setItemsPage(newPage);
    setItemsRowsPerPage(newRowsPerPage);
    handleViewItems(selectedStrain, newPage, newRowsPerPage);
  };

  const handleBackToStrains = () => {
    setOpenItemsPopup(false);
    setItemsData([]);
    setSelectedStrain(null);
  };

  const sChipsProps = farm => ({
    key: farm.id,
    label: farm.farm_name,
    variant: selectedFilter === farm.id ? 'filled' : 'outlined',
    onClick: () => handleFilterChange(farm.id),
    clickable: true,
    className: `${roomDetailClasses?.chip} ${currDarkThemeClass}`,
  });

  const metrcTagFilterWrapperProps = {
    direction: 'row',
    alignItems: 'center',
    width: '100%',
  };

  const metrcTagFarmChipsList = farms.map(farm => <Chip key={farm.id} {...sChipsProps(farm)} sx={{ flexShrink: 0 }} />);

  const outlinedInputProps = {
    className: `${roomDetailClasses.filterInput} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search for Strains',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    ),
    onChange: handleSearch,
    onKeyPress: handleKeyPress,
    value: searchTerm,
  };

  const strainTableValues = {
    animationClass: 'farmsTabContent',
    currDarkThemeClass,
    emptyTableMsg: !strainPerms?.read ? ` ${noPermTablePrefixMsg} strains` : 'No Strains Found',
    entity: 'Strains',
    tableEntries: data,
    tableTitles: [{ title: 'Sl. No' }, ...strainKeyTitleMap.map(e => ({ title: e.title })), { title: '' }],
    toShowAddBtn: false,
    page,
    rowsPerPage,
    totalCount,
    onPageChange: handlePageChange,
    loading,
    onClick: handleAddNewStrain,
    ...otherProps,
  };

  const strainsPopupProps = {
    currDarkThemeClass,
    open: openStrainsPopup || open,
    farms,

    handleAddStrain: handleEntitySave('strain/', 'strains'),
    onClose: handlePopupClose,
    ...detailChildren,
  };

  const handleSyncToMetrc = async () => {
    if (selectedFarm) {
      setSyncLoading(true);
      let syncTometrcUrl = `strain/?licenseNumber=${selectedFarm.license_number}&sync_to_metrc=true`;

      try {
        const response = await APIService.fetchInstance(syncTometrcUrl);
        if (response.status === 200) {
          fetchData();
          feedbackCX.setContextValue(true, {
            type: 'success',
            message: 'Strains Synced From Metrc Successfully.',
          });
          setTimeout(() => {
            feedbackCX.setContextValue(false, {});
          }, 5000);
        }
      } catch (error) {
        feedbackCX.setContextValue(true, {
          type: 'error',
          message: `Error syncing to Metrc`,
        });
      } finally {
        setSyncLoading(false);
      }
    }
  };

  const metrcTagFilterTypoProps = {
    className: `${metrcTagsClasses?.filterTitleTypo} ${currDarkThemeClass}`,
  };

  const metrcTagFilterProps = {
    direction: 'row',
    spacing: 3,
    alignItems: 'center',
  };

  if (!strainPerms?.read) return <NoPermissionMsg entity="Strains" />;

  return (
    <>
      <Paper className={`${farmsClasses?.tableListPaper} ${currDarkThemeClass} farmsTabContent`} square variant="none" style={{ marginTop: '10px', maxWidth: "100%" }} >
        <Stack {...metrcTagFilterWrapperProps} padding={2}>
          <Stack {...metrcTagFilterProps} sx={{ flex: 1, minWidth: 0 }}>
            <Typography {...metrcTagFilterTypoProps} sx={{ whiteSpace: 'nowrap' }}>Show by Farm:</Typography>
            <Box
              sx={{
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  gap: 1,
                  minWidth: 'max-content',
                  pb: 0.5,
                }}
              >
                {metrcTagFarmChipsList}
              </Box>
            </Box>
          </Stack>
          <Stack direction='row' sx={{ justifyContent: 'flex-end' }}>
            {selectedFilter !== 'All' && (
              <Button
                onClick={handleSyncToMetrc}
                disabled={syncLoading} style={{ display: 'flex', alignItems: 'center', paddingRight: '20px' }} sx={{ color: '#B1D23A', '&.Mui-disabled': { color: '#B1D23A', }, }}>
                <MetrcLogo style={{ width: 24, height: 24, marginRight: 8 }} />
                {syncLoading ? 'Syncing...' : 'Sync From Metrc'}
              </Button>
            )}
            <OutlinedInput {...outlinedInputProps} />
          </Stack>
        </Stack>
        <Box className={`${farmsClasses?.tableContainer} ${currDarkThemeClass}`}>
          {loading ? (
            <LoadingSpinner currDarkThemeClass={currDarkThemeClass} message="Loading Strains Data" />
          ) : (
            <DisplayTable
              tableTitles={strainTableValues.tableTitles}
              tableEntries={data}
              currDarkThemeClass={currDarkThemeClass}
              toShowAddBtn={false}
              page={page}
              rowsPerPage={rowsPerPage}
              count={totalCount}
              handleChangePage={(e, newPage) =>
                handlePageChange(newPage, rowsPerPage)
              }
              handleChangeRowsPerPage={(e) =>
                handlePageChange(0, +e.target.value)
              }
              emptyTableMsg={strainTableValues.emptyTableMsg}
              entity={strainTableValues.entity}
            />
          )}
        </Box>
        {(open && addNewKey) || openStrainsPopup && <CreateStrainPopup {...strainsPopupProps} />}
        {open && updateKey && <StrainEntryPopup {...strainsPopupProps} />}
      </Paper>
      <EntityDetailPopup
        open={openItemsPopup}
        onClose={() => setOpenItemsPopup(false)}
        title={`Strain - ${selectedStrain?.strain_name || ''}`}
        currDarkThemeClass={currDarkThemeClass}
        toShowSave={false}
        toShowValidate={false}
        headContentText=""
      >

        <Box p={2}>
          {itemsLoading ? (
            <LoadingSpinner message="Loading Items..." />
          ) : (
            <DisplayTable
              tableTitles={[
                { title: 'Sl. No' },
                { title: 'Item Name' },
                { title: 'Category' },
                { title: 'Unit' },
              ]}
              tableEntries={itemsData.map((item, idx) => [
                idx + 1 + itemsPage * itemsRowsPerPage,
                item.item_name,
                item.category_name,
                item.unit_name,
              ])}
              currDarkThemeClass={currDarkThemeClass}
              entity="Items"
              toShowAddBtn={false}

              page={itemsPage}
              rowsPerPage={itemsRowsPerPage}
              count={itemsTotalCount}
              handleChangePage={(e, newPage) =>
                handleItemsPageChange(newPage, itemsRowsPerPage)
              }
              handleChangeRowsPerPage={(e) =>
                handleItemsPageChange(0, +e.target.value)
              }
            />
          )}
        </Box>

      </EntityDetailPopup>
    </>
  );
}
